import { onMounted, onBeforeUnmount, watch } from 'vue'

/**
 * Composable pour gérer le focus trap dans les modals
 * Limite: 100 lignes max
 */
export function useFocusTrap(containerRef, isActive) {
  let previousActiveElement = null
  let focusableElements = []

  const getFocusableElements = () => {
    if (!containerRef.value) return []
    
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')
    
    return Array.from(containerRef.value.querySelectorAll(selector))
      .filter(el => {
        const style = window.getComputedStyle(el)
        return style.display !== 'none' && style.visibility !== 'hidden'
      })
  }

  const trapFocus = (e) => {
    if (!isActive.value || !containerRef.value) return
    
    focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }
  }

  // Pas de traitement d'Échap ici : c'est la fenêtre qui décide de se fermer
  // (croix seulement, garde de saisie…). Rendre le focus au déclencheur sur
  // Échap l'envoyait derrière une fenêtre restée ouverte ; `deactivate` le
  // rend à la vraie fermeture.

  const activate = () => {
    if (!containerRef.value) return
    
    previousActiveElement = document.activeElement
    focusableElements = getFocusableElements()
    
    // Focus sur le premier élément focusable ou le container — sauf si la
    // fenêtre a déjà placé le focus chez elle (un champ qui se focalise seul).
    const firstElement = focusableElements[0] || containerRef.value
    if (firstElement) {
      setTimeout(() => {
        if (containerRef.value?.contains(document.activeElement)) return
        firstElement.focus()
      }, 0)
    }

    document.addEventListener('keydown', trapFocus)
  }

  const deactivate = () => {
    document.removeEventListener('keydown', trapFocus)
    
    if (previousActiveElement) {
      previousActiveElement.focus()
      previousActiveElement = null
    }
  }

  // `flush: 'post'` : le panneau est rendu sous `v-if`. Lu avant la mise à jour
  // du DOM, `containerRef` valait encore null à l'ouverture, `activate` sortait
  // sans rien faire, et aucune fenêtre ouverte après le montage n'avait ni
  // focus initial ni piège à Tab : le focus restait sur le déclencheur, et Tab
  // parcourait la page derrière la fenêtre.
  watch(isActive, (newValue) => {
    if (newValue) {
      activate()
    } else {
      deactivate()
    }
  }, { flush: 'post' })

  onMounted(() => {
    if (isActive.value) {
      activate()
    }
  })

  onBeforeUnmount(() => {
    deactivate()
  })

  return {
    activate,
    deactivate
  }
}
